import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { bootOnce } from '../../bootstrap';
import { useI18n } from '../../bootstrap/i18n';
import { trackEvent, trackPackageSelected, trackPaywallDismissed, trackPremiumOpen, trackSubscriptionPaymentFailed, trackSubscriptionPaymentInitiated } from '../../analytics';
import { IconClose, IconCopy, IconSearch, IconStar } from '../../ui/icons';
import { BottomSheet } from '../../ui/sheets/BottomSheet';
import { getTelegramId, requestAuthRequired } from '../../utils/telegramUser';
import { apiFetch, getAccessTokenOrNull } from '../../utils/api';
import { reportHandledError } from '../../monitoring/uiErrorSink';
import { getClientSessionId } from '../../utils/clientSession';

type SubscriptionPlan = {
  id: number;
  name: string;
  price_stars: number;
  gross_amount_usd: number;
  price_amount: number;
  price_currency: string;
  bonus_credits: number;
  duration_days: number;
  stars_is_recurring?: boolean;
  is_popular?: boolean;
};

type CryptoSession = {
  order_id: string | null;
  payment_id: string | null;
  status: string;
  pay_currency: string | null;
  network: string | null;
  pay_amount: number | null;
  pay_address: string | null;
  payin_extra_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  expires_at: string | null;
};

type CryptoAsset = {
  pay_currency: string;
  code: string;
  ticker: string | null;
  name: string | null;
  network: string | null;
  logo_url: string | null;
  is_popular: boolean;
  is_stable: boolean;
};

function shortenMiddle(raw: string, head = 6, tail = 6): string {
  const value = String(raw || '').trim();
  if (!value) return '';
  if (value.length <= head + tail + 2) return value;
  return `${value.slice(0, head)}…${value.slice(-tail)}`;
}

function parseNowPaymentsPlanIdFromOrderId(raw: string | null | undefined): string | null {
  const value = String(raw || '').trim();
  if (!value) return null;
  // Format: np_{user8}_{planId}_{timestamp}
  const parts = value.split('_');
  if (parts.length >= 4 && parts[0] === 'np') {
    const planId = String(parts[2] || '').trim();
    return planId || null;
  }
  return null;
}

function networkBadgeLabel(raw: string | null | undefined): string | null {
  const n = String(raw || '').trim().toLowerCase();
  if (!n) return null;
  if (n === 'eth' || n === 'ethereum') return 'ETH';
  if (n === 'trx' || n === 'trc' || n === 'trc20' || n === 'tron') return 'TRX';
  if (n === 'bsc' || n === 'bnb' || n === 'bep20') return 'BSC';
  if (n === 'sol' || n === 'solana') return 'SOL';
  if (n === 'ton') return 'TON';
  if (n === 'arb' || n === 'arbitrum') return 'ARB';
  if (n === 'op' || n === 'optimism') return 'OP';
  if (n === 'matic' || n === 'polygon') return 'POLY';
  if (n === 'avax' || n === 'avalanche') return 'AVAX';
  if (n === 'ltc') return 'LTC';
  if (n === 'btc' || n === 'bitcoin') return 'BTC';
  return n.toUpperCase().slice(0, 6);
}

export type PremiumSheetContext =
  | { origin: 'profile' | 'storefront' | 'onboarding' | 'surprise_offer' | 'surprise_offer_upsell' | 'premium' }
  | { origin: 'chat'; commandName: string; modelName?: string };

const PLANS_TTL_MS = 60_000;
const CARD_PENDING_TTL_MS = 10 * 60 * 1000;
const CRYPTO_PENDING_STORAGE_KEY = 'pendingCryptoPayment';
const CRYPTO_PENDING_STATUSES = new Set(['waiting', 'confirming', 'confirmed', 'sending', 'partially_paid']);
let plansCache: { at: number; rows: SubscriptionPlan[] } | null = null;

function getFreshPlansCache(): SubscriptionPlan[] | null {
  const c = plansCache;
  if (!c) return null;
  if (Date.now() - c.at > PLANS_TTL_MS) return null;
  if (!Array.isArray(c.rows) || c.rows.length === 0) return null;
  return c.rows;
}

function readPendingCardPaymentForPlan(planId: number | null | undefined): { orderId: string; paymentUrl: string; createdAt: number } | null {
  try {
    const pendingStr = localStorage.getItem('pendingCardPayment');
    if (!pendingStr) return null;
    const pending = JSON.parse(pendingStr);
    const orderId = String(pending?.order_id || '').trim();
    const paymentUrl = String(pending?.payment_url || '').trim();
    const createdAt = Number(pending?.created_at || 0);
    if (!orderId || !paymentUrl || !createdAt || !Number.isFinite(createdAt)) {
      localStorage.removeItem('pendingCardPayment');
      return null;
    }
    if (Date.now() - createdAt > CARD_PENDING_TTL_MS) {
      localStorage.removeItem('pendingCardPayment');
      return null;
    }
    if (!planId) return null;
    if (String(pending?.plan_id || '') !== String(planId)) return null;
    return { orderId, paymentUrl, createdAt };
  } catch {
    try { localStorage.removeItem('pendingCardPayment'); } catch {}
    return null;
  }
}

function normalizePlans(plans: any[]): SubscriptionPlan[] {
  return (plans || []).map((p) => ({
    id: Number(p.id),
    name: String(p.name || ''),
    price_stars: Number(p.price_stars || 0),
    gross_amount_usd: Number(p.gross_amount_usd || 0),
    price_amount: Number(p.price_amount || 0),
    price_currency: String(p.price_currency || 'USD'),
    bonus_credits: Number(p.bonus_credits || 0),
    duration_days: Number(p.duration_days || 30),
    stars_is_recurring: p.stars_is_recurring === true,
    is_popular: !!p.is_popular,
  }));
}

function fmtMoney(amount: number, currency: string): string {
  const v = Number(amount || 0);
  const cur = String(currency || '').trim().toUpperCase() || 'USD';
  const sym = cur === 'EUR' ? '€' : '$';
  if (!Number.isFinite(v) || v <= 0) return `${sym}0.00`;
  return `${sym}${v.toFixed(2)}`;
}

export function PremiumSheet(props: {
  open: boolean;
  context: PremiumSheetContext;
  onClose: () => void;
}) {
  if (!props.open) return null;
  const services = useMemo(() => bootOnce(), []);
  const { t, locale } = useI18n(services.i18n);

  const initialCached = getFreshPlansCache();
  const [loading, setLoading] = useState(!initialCached);
  const [plansErr, setPlansErr] = useState<string | null>(null);
  const [paymentErr, setPaymentErr] = useState<string | null>(null);
  const [cryptoAssetPickerOpen, setCryptoAssetPickerOpen] = useState(false);
  const [cryptoAssetQuery, setCryptoAssetQuery] = useState('');
  const [cryptoAssets, setCryptoAssets] = useState<CryptoAsset[] | null>(null);
  const [cryptoAssetsLoading, setCryptoAssetsLoading] = useState(false);
  const [cryptoAssetsErr, setCryptoAssetsErr] = useState<string | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>(initialCached || []);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [payState, setPayState] = useState<'ready' | 'preparing'>('ready');
  const [reloadKey, setReloadKey] = useState(0);
  const [paymentStep, setPaymentStep] = useState<'cta' | 'methods' | 'crypto'>('cta');
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'info' | 'success'>('info');
  const [cryptoSession, setCryptoSession] = useState<CryptoSession | null>(null);
  const [cryptoRefreshTick, setCryptoRefreshTick] = useState(0);
  const [cryptoPendingCreatedAtMs, setCryptoPendingCreatedAtMs] = useState<number | null>(null);
  const [cryptoLastCheckedAtMs, setCryptoLastCheckedAtMs] = useState<number | null>(null);
  const [cryptoLastCheckError, setCryptoLastCheckError] = useState<string | null>(null);
  const [cryptoManualChecking, setCryptoManualChecking] = useState(false);
  const lastCancelledCryptoOrderRef = useRef<string | null>(null);
  const [hasPendingCard, setHasPendingCard] = useState(false);
  const [cardPaymentResult, setCardPaymentResult] = useState<'success' | 'failed' | null>(null);
  const [currentOrderUuid, setCurrentOrderUuid] = useState<string | null>(null);
  const pollingIntervalRef = useRef<any>(null);
  const pollingTimeoutRef = useRef<any>(null);

  const [offer, setOffer] = useState<any>(null);
  const [offerLoading, setOfferLoading] = useState(false);
  const [offerExpiresAt, setOfferExpiresAt] = useState<number>(0);
  const [offerGraceExpiresAt, setOfferGraceExpiresAt] = useState<number>(0);
  const [offerSecondsLeft, setOfferSecondsLeft] = useState<number>(0);
  const [offerGraceSecondsLeft, setOfferGraceSecondsLeft] = useState<number>(0);
  const didTrackOfferViewRef = useRef(false);
  const didTrackPlanSelectedRef = useRef(false);
  const openedAtRef = useRef<number | null>(null);
  const didAttemptPayRef = useRef(false);
  const didPayRef = useRef(false);
  const lastCryptoStatusRef = useRef<string | null>(null);
  const lastCryptoViewOrderRef = useRef<string | null>(null);

  const selectedPlan = useMemo(() => plans.find(p => p.id === selectedId), [plans, selectedId]);
  const isLifetime = selectedPlan && (selectedPlan.duration_days || 0) >= 3650;

  const hasPendingCrypto = useMemo(() => {
    const status = String(cryptoSession?.status || '').trim().toLowerCase();
    return !!cryptoSession?.order_id && CRYPTO_PENDING_STATUSES.has(status);
  }, [cryptoSession]);

  const hasPendingCryptoForSelectedPlan = useMemo(() => {
    if (!hasPendingCrypto) return false;
    if (!selectedPlan?.id) return hasPendingCrypto;
    const planIdFromOrder = parseNowPaymentsPlanIdFromOrderId(cryptoSession?.order_id);
    if (!planIdFromOrder) return hasPendingCrypto;
    return String(planIdFromOrder) === String(selectedPlan.id);
  }, [hasPendingCrypto, cryptoSession?.order_id, selectedPlan?.id]);

  function cryptoStatusLabel(statusRaw: string | null | undefined): string {
    const status = String(statusRaw || '').trim().toLowerCase();
    if (status === 'waiting') return t('premium.crypto_status_waiting');
    if (status === 'confirming') return t('premium.crypto_status_confirming');
    if (status === 'confirmed') return t('premium.crypto_status_confirmed');
    if (status === 'sending') return t('premium.crypto_status_sending');
    if (status === 'partially_paid') return t('premium.crypto_status_partially_paid');
    if (status === 'finished') return t('premium.crypto_status_finished');
    if (status === 'failed') return t('premium.crypto_status_failed');
    if (status === 'expired') return t('premium.crypto_status_expired');
    return status ? status : t('premium.crypto_status_unknown');
  }

  function trackCryptoPaywallEvent(action: string, extraMetadata?: Record<string, any>): void {
    try {
      const selectionKind: 'explicit' | 'implicit' = didTrackPlanSelectedRef.current ? 'explicit' : 'implicit';
      const normalizedStatus = String(cryptoSession?.status || '').trim().toLowerCase() || null;
      trackEvent('crypto_paywall_event', {
        origin: props.context.origin,
        metadata: {
          ...(selectedPlan ? premiumAnalyticsMeta(selectedPlan, selectionKind) : {}),
          flow: 'premium',
          provider: 'nowpayments',
          payment_method: 'crypto',
          provider_payment_method: 'nowpayments',
          action: String(action || '').trim() || 'unknown',
          order_id: cryptoSession?.order_id || null,
          payment_id: cryptoSession?.payment_id || null,
          pay_currency: cryptoSession?.pay_currency || null,
          network: cryptoSession?.network || null,
          status: normalizedStatus,
          ...(extraMetadata && typeof extraMetadata === 'object' ? extraMetadata : {}),
        },
      });
    } catch {}
  }

  async function copyText(value: string, label: string, field?: 'amount' | 'address' | 'memo'): Promise<void> {
    const text = String(value || '').trim();
    if (!text) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.focus();
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setToastType('info');
      setToastMessage(t('premium.crypto_copied').replace('{label}', label));
      setTimeout(() => setToastMessage(null), 1800);
      const copyAction =
        field === 'amount' ? 'copy_amount' :
        field === 'address' ? 'copy_address' :
        field === 'memo' ? 'copy_memo' :
        'copy';
      trackCryptoPaywallEvent(copyAction, {
        field: field || null,
      });
    } catch (e) {
      console.warn('[crypto] copy failed:', e);
    }
  }

  function persistPendingCrypto(orderId: string | null, createdAtMs?: number | null): void {
    if (!orderId) return;
    const ms = Number.isFinite(Number(createdAtMs)) ? Number(createdAtMs) : Date.now();
    setCryptoPendingCreatedAtMs(ms);
    try {
      localStorage.setItem(CRYPTO_PENDING_STORAGE_KEY, JSON.stringify({
        order_id: orderId,
        created_at: ms,
      }));
    } catch {}
  }

  function clearPendingCrypto(): void {
    try { localStorage.removeItem(CRYPTO_PENDING_STORAGE_KEY); } catch {}
    setCryptoPendingCreatedAtMs(null);
  }

  async function cancelCryptoSession(orderIdRaw: string | null | undefined, reason: 'switch_method' | 'plan_changed') {
    const orderId = String(orderIdRaw || '').trim();
    if (!orderId) return;
    if (lastCancelledCryptoOrderRef.current === orderId) return;
    lastCancelledCryptoOrderRef.current = orderId;
    try {
      const jwt = await getAccessTokenOrNull(services);
      if (!jwt) return;
      const res = await apiFetch('/api/nowpayments/session-cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ order_id: orderId, reason }),
      });
      const data = await res.json().catch(() => ({}));
      trackCryptoPaywallEvent('session_cancelled', {
        reason,
        order_id: orderId,
        cancelled: Boolean(data?.cancelled),
        http_status: res.status,
      });
    } catch (e) {
      console.warn('[crypto] cancel failed:', e);
      trackCryptoPaywallEvent('session_cancel_failed', {
        reason,
        order_id: orderId,
        error: String((e as any)?.message || e || ''),
      });
    }
  }

  async function fetchCryptoSessionStatus(orderId: string): Promise<CryptoSession | null> {
    const safeOrderId = String(orderId || '').trim();
    if (!safeOrderId) return null;

    const jwt = await getAccessTokenOrNull(services);
    if (!jwt) return null;

    const res = await apiFetch(`/api/nowpayments/session-status?order_id=${encodeURIComponent(safeOrderId)}`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok && data?.ok && String(data?.status || '').toLowerCase() === 'not_found') {
      return {
        order_id: safeOrderId,
        payment_id: null,
        status: 'not_found',
        pay_currency: null,
        network: null,
        pay_amount: null,
        pay_address: null,
        payin_extra_id: null,
        created_at: null,
        updated_at: null,
        expires_at: null,
      };
    }
    if (!res.ok || !data?.ok) return null;
    if (!data?.session?.order_id) return null;

    return {
      order_id: String(data.session.order_id || ''),
      payment_id: data.session.payment_id ? String(data.session.payment_id) : null,
      status: String(data.session.status || 'waiting'),
      pay_currency: data.session.pay_currency ? String(data.session.pay_currency) : null,
      network: data.session.network ? String(data.session.network) : null,
      pay_amount: Number.isFinite(Number(data.session.pay_amount)) ? Number(data.session.pay_amount) : null,
      pay_address: data.session.pay_address ? String(data.session.pay_address) : null,
      payin_extra_id: data.session.payin_extra_id ? String(data.session.payin_extra_id) : null,
      created_at: data.session.created_at ? String(data.session.created_at) : null,
      updated_at: data.session.updated_at ? String(data.session.updated_at) : null,
      expires_at: data.session.expires_at ? String(data.session.expires_at) : null,
    };
  }

  useEffect(() => {
    try {
      const isChat = props.context.origin === 'chat';
      trackPremiumOpen(props.context.origin, {
        origin: props.context.origin,
        command_name: isChat ? (props.context as any).commandName : null,
        model_name: isChat ? ((props.context as any).modelName ?? null) : null,
      });
    } catch {}
  }, []);

  // Polling for card payment status (with specific CentroBill order_id)
  useEffect(() => {
    if (!hasPendingCard) return;
    if (!currentOrderUuid) return;

    const checkPaymentStatus = async () => {
      try {
        const token = await getAccessTokenOrNull(services);
        if (!token) return;

        const url = `/api/centrobill/order-status?order_id=${encodeURIComponent(currentOrderUuid)}`;

        const res = await apiFetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();

        if (data?.ok && data?.status) {
          if (data.status === 'paid') {
            setCardPaymentResult('success');
            setHasPendingCard(false);
            setCurrentOrderUuid(null);
            try { localStorage.removeItem('pendingCardPayment'); } catch {}
            // Refresh user data
            setReloadKey((k) => k + 1);
          } else if (data.status === 'failed' || data.status === 'cancelled') {
            setCardPaymentResult('failed');
            setHasPendingCard(false);
            setCurrentOrderUuid(null);
            try { localStorage.removeItem('pendingCardPayment'); } catch {}
          } else if (data.status === 'expired') {
            setHasPendingCard(false);
            setCurrentOrderUuid(null);
            try { localStorage.removeItem('pendingCardPayment'); } catch {}
          }
          // Keep polling if status is 'pending'
        }
      } catch (err) {
        console.error('[card] polling error:', err);
      }
    };

    // Poll every 3 seconds
    pollingIntervalRef.current = setInterval(checkPaymentStatus, 3000);
    // Also check immediately
    checkPaymentStatus();

    // Timeout after 10 minutes - stop polling and show timeout message
    pollingTimeoutRef.current = setTimeout(() => {
      console.log('[card] polling timeout after 10 minutes');
      setHasPendingCard(false);
      setCurrentOrderUuid(null);
      try { localStorage.removeItem('pendingCardPayment'); } catch {}
      // Don't show error - user can try again
    }, 10 * 60 * 1000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [hasPendingCard, currentOrderUuid, services]);

  // Auto-hide card payment result toast and close sheet on success
  useEffect(() => {
    if (!cardPaymentResult) return;

    const timer = setTimeout(() => {
      if (cardPaymentResult === 'success') {
        props.onClose();
      }
      setCardPaymentResult(null);
    }, 1500);

    return () => clearTimeout(timer);
  }, [cardPaymentResult, props.onClose]);

  // Check for pending crypto payment on mount/open
  useEffect(() => {
    if (!props.open) return;

    const restorePendingCrypto = async () => {
      try {
        const pendingRaw = localStorage.getItem(CRYPTO_PENDING_STORAGE_KEY);
        if (!pendingRaw) {
          setCryptoSession(null);
          return;
        }

        const pending = JSON.parse(pendingRaw);
        const orderId = String(pending?.order_id || '').trim();
        const createdAtMs = Number(pending?.created_at || 0);
        if (!orderId) {
          clearPendingCrypto();
          setCryptoSession(null);
          return;
        }
        if (Number.isFinite(createdAtMs) && createdAtMs > 0) {
          setCryptoPendingCreatedAtMs(createdAtMs);
        }

        if (Number.isFinite(createdAtMs) && createdAtMs > 0 && (Date.now() - createdAtMs > 20 * 60 * 1000)) {
          clearPendingCrypto();
          setCryptoSession(null);
          return;
        }

        const session = await fetchCryptoSessionStatus(orderId);
        if (!session) {
          // Keep pending marker so user can retry manual refresh.
          setCryptoSession({
            order_id: orderId,
            payment_id: null,
            status: 'waiting',
            pay_currency: null,
            network: null,
            pay_amount: null,
            pay_address: null,
            payin_extra_id: null,
            created_at: null,
            updated_at: null,
            expires_at: null,
          });
          trackCryptoPaywallEvent('session_restore_unavailable', {
            order_id: orderId,
          });
          return;
        }

        setCryptoSession(session);
        setCryptoLastCheckedAtMs(Date.now());
        setCryptoLastCheckError(null);
        const restoredStatus = String(session.status || '').trim().toLowerCase() || 'unknown';
        trackCryptoPaywallEvent('session_restored', {
          order_id: session.order_id || orderId,
          payment_id: session.payment_id || null,
          pay_currency: session.pay_currency || null,
          network: session.network || null,
          status: restoredStatus,
        });
        lastCryptoStatusRef.current = restoredStatus;

        if (session.status === 'finished') {
          clearPendingCrypto();
          setToastType('success');
          setToastMessage(t('premium.payment_success'));
          setTimeout(() => {
            setToastMessage(null);
            setToastType('info');
          }, 5000);

          try { window.dispatchEvent(new Event('aicams-subscription-refresh')); } catch {}
          try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {}

          setTimeout(() => {
            props.onClose();
          }, 2000);
          return;
        }

        if (!CRYPTO_PENDING_STATUSES.has(String(session.status || '').toLowerCase())) {
          clearPendingCrypto();
        }
      } catch (e) {
        console.error('[crypto] pending check error:', e);
      }
    };

    restorePendingCrypto();
  }, [props.open]);

  // Check for pending card payment on mount/open (CentroBill)
  useEffect(() => {
    if (!props.open) return;
    const pending = readPendingCardPaymentForPlan(selectedPlan?.id);
    setHasPendingCard(!!pending);
    setCurrentOrderUuid(pending?.orderId || null);
  }, [props.open, selectedPlan?.id]);

  async function applyCryptoSessionUpdate(session: CryptoSession, source: 'auto' | 'manual' | 'restore') {
    setCryptoSession(session);
    setCryptoLastCheckedAtMs(Date.now());
    setCryptoLastCheckError(null);
    const nextStatus = String(session.status || '').trim().toLowerCase() || 'unknown';
    const prevStatus = String(lastCryptoStatusRef.current || '').trim().toLowerCase() || null;
    if (prevStatus !== nextStatus) {
      trackCryptoPaywallEvent('status_changed', {
        source,
        previous_status: prevStatus,
        status: nextStatus,
        order_id: session.order_id || null,
        payment_id: session.payment_id || null,
      });
      lastCryptoStatusRef.current = nextStatus;
    }

    if (session.status === 'finished') {
      clearPendingCrypto();
      setToastType('success');
      setToastMessage(t('premium.payment_success'));
      setTimeout(() => {
        setToastMessage(null);
        setToastType('info');
      }, 5000);
      try { window.dispatchEvent(new Event('aicams-subscription-refresh')); } catch {}
      try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {}
      setTimeout(() => props.onClose(), 2000);
      return;
    }

    if (!CRYPTO_PENDING_STATUSES.has(String(session.status || '').toLowerCase())) {
      clearPendingCrypto();
      // Keep session visible on screen even if we clear pending marker.
    }
  }

  async function checkCryptoStatusNow(source: 'auto' | 'manual' | 'restore') {
    const orderId = String(cryptoSession?.order_id || '').trim();
    if (!orderId) return;
    if (source === 'manual') setCryptoManualChecking(true);
    try {
      const session = await fetchCryptoSessionStatus(orderId);
      if (!session) {
        trackCryptoPaywallEvent('status_check_failed', {
          source,
          order_id: orderId,
        });
        setCryptoLastCheckedAtMs(Date.now());
        setCryptoLastCheckError('check_failed');
        return;
      }
      await applyCryptoSessionUpdate(session, source);
    } catch (e) {
      console.error('[crypto] check error:', e);
      trackCryptoPaywallEvent('status_check_error', {
        source,
        order_id: orderId,
        error: String((e as any)?.message || e || ''),
      });
      setCryptoLastCheckedAtMs(Date.now());
      setCryptoLastCheckError(String((e as any)?.message || 'check_error'));
    } finally {
      if (source === 'manual') setCryptoManualChecking(false);
    }
  }

  // Polling for pending crypto payment status (adaptive intervals)
  useEffect(() => {
    if (!hasPendingCrypto || !props.open || !cryptoSession?.order_id) return;
    let cancelled = false;
    let timeoutId: number | null = null;

    const computeDelayMs = () => {
      const createdMs = cryptoPendingCreatedAtMs
        ?? (cryptoSession?.created_at ? new Date(cryptoSession.created_at).getTime() : null)
        ?? null;
      const ageMs = (createdMs && Number.isFinite(createdMs)) ? Math.max(0, Date.now() - createdMs) : 0;
      if (ageMs < 2 * 60 * 1000) return 5000;
      if (ageMs < 10 * 60 * 1000) return 15000;
      return 30000;
    };

    const loop = async () => {
      if (cancelled) return;
      await checkCryptoStatusNow('auto');
      if (cancelled) return;
      timeoutId = window.setTimeout(loop, computeDelayMs());
    };

    void loop();
    return () => {
      cancelled = true;
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [hasPendingCrypto, props.open, cryptoSession?.order_id, cryptoPendingCreatedAtMs, cryptoRefreshTick]);

  // Fetch subscription plans
  useEffect(() => {
    if (!props.open) return;
    setPlansErr(null);
    const cached = getFreshPlansCache();
    if (cached && cached.length > 0) {
      setPlans(cached);
      setSelectedId(cached.find(p => p.is_popular)?.id ?? cached[0]?.id ?? null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const tmr = window.setTimeout(() => { try { ctrl?.abort(); } catch {} }, 8000);

    // IMPORTANT: use relative URL to avoid CORS/mismatch across envs.
    apiFetch('/api/subscription-plans', { signal: (ctrl as any)?.signal })
      .then(async (res) => {
        const ct = String(res.headers.get('content-type') || '').toLowerCase();
        if (!ct.includes('application/json')) {
          const txt = await res.text().catch(() => '');
          throw new Error(`plans_bad_content_type:${res.status}:${ct || 'none'}:${txt ? 'body' : 'no_body'}`);
        }
        const apiData: any = await res.json().catch(() => ({}));
        if (!res.ok || !apiData?.ok) throw new Error(String(apiData?.error || 'plans_failed'));
        return apiData;
      })
      .then((apiData: any) => {
        const rows = normalizePlans(apiData?.data || []);
        if (!rows.length) throw new Error('plans_empty');
        plansCache = { at: Date.now(), rows };
        setPlans(rows);
        setSelectedId(rows.find(p => p.is_popular)?.id ?? rows[0]?.id ?? null);
      })
      .catch((e) => {
        const msg = String(e?.message || e || '');
        const friendly =
          msg.includes('abort') ? t('premium.plans_timeout') :
          msg.startsWith('plans_bad_content_type') ? t('premium.plans_error') :
          msg === 'plans_empty' ? t('premium.plans_error') :
          t('premium.plans_error');
        setPlansErr(reportHandledError({
          err: e,
          kind: 'api',
          code: 'premium.plans_failed',
          userMessage: friendly,
          severity: 'warning',
          context: { feature: 'premium', endpoint: '/api/subscription-plans' },
        }));
      })
      .finally(() => {
        try { window.clearTimeout(tmr); } catch {}
        setLoading(false);
      });
  }, [props.open, reloadKey]);

  // Premium Offer timer (FREE users only; backend enforces eligibility)
  useEffect(() => {
    if (!props.open) return;
    let cancelled = false;
    (async () => {
      setOfferLoading(true);
      const apiBase = services.config.config.apiBaseUrl || '';
      const jwt = await getAccessTokenOrNull(services);
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (jwt) headers['Authorization'] = `Bearer ${jwt}`;
      try {
        await apiFetch(`${apiBase}/api/user/premium-offer/start`, { method: 'POST', headers });
      } catch {}
      try {
        const res = await apiFetch(`${apiBase}/api/user/premium-offer`, { method: 'GET', headers });
        if (cancelled) return;
        const data = await res.json().catch(() => ({}));
        setOffer(data);
      } catch {
        if (cancelled) return;
        setOffer(null);
      } finally {
        if (cancelled) return;
        setOfferLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [props.open]);

  // Initialize timestamps from API response
  useEffect(() => {
    if (!offer) {
      setOfferExpiresAt(0);
      setOfferGraceExpiresAt(0);
      setOfferSecondsLeft(0);
      setOfferGraceSecondsLeft(0);
      return;
    }
    const expiresAtMs = offer.expires_at ? new Date(offer.expires_at).getTime() : 0;
    const graceExpiresAtMs = offer.grace_expires_at ? new Date(offer.grace_expires_at).getTime() : 0;
    setOfferExpiresAt(expiresAtMs);
    setOfferGraceExpiresAt(graceExpiresAtMs);
    const now = Date.now();
    setOfferSecondsLeft(Math.max(0, Math.ceil((expiresAtMs - now) / 1000)));
    setOfferGraceSecondsLeft(Math.max(0, Math.ceil((graceExpiresAtMs - now) / 1000)));
  }, [offer]);

  // Timer countdown effect - recalculate from timestamps every second
  useEffect(() => {
    if (!props.open) return;
    if (offerExpiresAt <= 0 && offerGraceExpiresAt <= 0) return;
    const tick = () => {
      const now = Date.now();
      const secLeft = Math.max(0, Math.ceil((offerExpiresAt - now) / 1000));
      const graceSecLeft = Math.max(0, Math.ceil((offerGraceExpiresAt - now) / 1000));
      setOfferSecondsLeft(secLeft);
      setOfferGraceSecondsLeft(graceSecLeft);
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => { try { window.clearInterval(id); } catch {} };
  }, [props.open, offerExpiresAt, offerGraceExpiresAt]);

  const offerState = useMemo(() => {
    if (offerLoading) return 'loading';
    // Grant-based persistent offer (no timer)
    if (offer?.state === 'grant_active' || offer?.has_grant) return 'grant_active';
    if (offerSecondsLeft > 0) return 'active';
    if (offerGraceSecondsLeft > 0) return 'grace';
    if (offerExpiresAt > 0 || offerGraceExpiresAt > 0) return 'expired';
    return 'inactive';
  }, [offerLoading, offer, offerSecondsLeft, offerGraceSecondsLeft, offerExpiresAt, offerGraceExpiresAt]);

  useEffect(() => {
    if (props.open) {
      didTrackPlanSelectedRef.current = false;
      openedAtRef.current = Date.now();
      didAttemptPayRef.current = false;
      didPayRef.current = false;
      lastCryptoStatusRef.current = null;
      lastCryptoViewOrderRef.current = null;
      setPaymentStep('cta');
      setPlansErr(null);
      setPaymentErr(null);
    }
  }, [props.open]);

  function offerExtrasForPlan(plan: SubscriptionPlan | null): { giftMonths: number; giftDays: number; bonusCredits: number } {
    if (!plan) return { giftMonths: 0, giftDays: 0, bonusCredits: 0 };
    if (!(offerState === 'active' || offerState === 'grace' || offerState === 'grant_active')) return { giftMonths: 0, giftDays: 0, bonusCredits: 0 };
    const d = Number(plan.duration_days || 0);
    const isDaily = d <= 7;
    const isYearly = d >= 300;
    if (isDaily) {
      const gd = Number(offer?.gift_days_daily || 0);
      const bc = Number(offer?.bonus_credits_daily || 0);
      return { giftMonths: 0, giftDays: Number.isFinite(gd) ? Math.max(0, Math.trunc(gd)) : 0, bonusCredits: Number.isFinite(bc) ? Math.max(0, Math.trunc(bc)) : 0 };
    }
    const gift = isYearly ? Number(offer?.gift_months_yearly || 0) : Number(offer?.gift_months_monthly || 0);
    const bonus = isYearly ? Number(offer?.bonus_credits_yearly || 0) : Number(offer?.bonus_credits_monthly || 0);
    const giftMonths = Number.isFinite(gift) ? Math.max(0, Math.trunc(gift)) : 0;
    const bonusCredits = Number.isFinite(bonus) ? Math.max(0, Math.trunc(bonus)) : 0;
    return { giftMonths, giftDays: 0, bonusCredits };
  }

  function premiumAnalyticsMeta(plan: SubscriptionPlan, selectionKind: 'explicit' | 'implicit') {
    const { giftMonths, bonusCredits } = offerExtrasForPlan(plan);
    const isOfferLive = offerState === 'active' || offerState === 'grace' || offerState === 'grant_active';
    const baseCredits = Number(plan.bonus_credits || 0) || 0;
    const offerBonusCredits = isOfferLive ? bonusCredits : 0;
    const offerGiftMonths = isOfferLive ? giftMonths : 0;
    const bonusCreditsTotal = baseCredits + offerBonusCredits;
    const isChat = props.context.origin === 'chat';

    return {
      product: 'premium',
      selection_kind: selectionKind,
      plan_id: plan.id,
      plan_name: plan.name,
      duration_days: plan.duration_days,
      pricing_amount: plan.price_amount,
      pricing_currency: plan.price_currency,
      gross_amount_usd: plan.gross_amount_usd,
      price_stars: plan.price_stars,
      offer_state: offerState,
      offer_bonus_credits: isOfferLive ? offerBonusCredits : null,
      offer_gift_months: isOfferLive ? offerGiftMonths : null,
      bonus_credits_base: baseCredits,
      bonus_credits_total: bonusCreditsTotal,
      command_name: isChat ? (props.context as any).commandName : null,
      model_name: isChat ? ((props.context as any).modelName ?? null) : null,
    };
  }

  // Track offer view once per open.
  useEffect(() => {
    if (!props.open) return;
    if (didTrackOfferViewRef.current) return;
    if (!(offerState === 'active' || offerState === 'grace' || offerState === 'grant_active')) return;
    didTrackOfferViewRef.current = true;
    void trackEvent('premium_offer_view', {
      origin: props.context.origin,
      metadata: {
        state: offerState,
        gift_months_monthly: offer?.gift_months_monthly ?? null,
        gift_months_yearly: offer?.gift_months_yearly ?? null,
      },
    });
  }, [props.open, offerState]);

  const giftMonthsForPlan = useMemo(() => {
    if (!(offerState === 'active' || offerState === 'grace' || offerState === 'grant_active')) return 0;
    const d = Number(selectedPlan?.duration_days || 0);
    if (d >= 3650) return 0;
    if (d <= 7) return 0;
    const isYearly = d >= 300;
    const gm = isYearly ? Number(offer?.gift_months_yearly || 0) : Number(offer?.gift_months_monthly || 0);
    return Number.isFinite(gm) ? Math.max(0, Math.trunc(gm)) : 0;
  }, [offer, selectedPlan, offerState]);

  const giftDaysForPlan = useMemo(() => {
    if (!(offerState === 'active' || offerState === 'grace' || offerState === 'grant_active')) return 0;
    const d = Number(selectedPlan?.duration_days || 0);
    if (d > 7) return 0;
    const gd = Number(offer?.gift_days_daily || 0);
    return Number.isFinite(gd) ? Math.max(0, Math.trunc(gd)) : 0;
  }, [offer, selectedPlan, offerState]);

  const bonusCreditsForPlan = useMemo(() => {
    if (!(offerState === 'active' || offerState === 'grace' || offerState === 'grant_active')) return 0;
    const d = Number(selectedPlan?.duration_days || 0);
    if (d >= 3650) return 0;
    if (d <= 7) {
      const bc = Number(offer?.bonus_credits_daily || 0);
      return Number.isFinite(bc) ? Math.max(0, Math.trunc(bc)) : 0;
    }
    const isYearly = d >= 300;
    const bc = isYearly ? Number(offer?.bonus_credits_yearly || 0) : Number(offer?.bonus_credits_monthly || 0);
    return Number.isFinite(bc) ? Math.max(0, Math.trunc(bc)) : 0;
  }, [offer, selectedPlan, offerState]);

  const saveStars = useMemo(() => {
    if (!selectedPlan) return 0;
    const monthly = plans
      .slice()
      .sort((a, b) => Number(a.duration_days || 0) - Number(b.duration_days || 0))[0];
    const d0 = Number(monthly?.duration_days || 0) || 0;
    const p0 = Number(monthly?.price_stars || 0) || 0;
    if (!monthly || d0 <= 0 || p0 <= 0) return 0;
    const rate = p0 / d0;
    const effDays = Number(selectedPlan.duration_days || 0) + (giftMonthsForPlan * 30) + giftDaysForPlan;
    if (!Number.isFinite(effDays) || effDays <= 0) return 0;
    const valueStars = Math.ceil(rate * effDays);
    const save = Math.max(0, valueStars - Number(selectedPlan.price_stars || 0));
    return Number.isFinite(save) ? Math.trunc(save) : 0;
  }, [plans, selectedPlan, giftMonthsForPlan, giftDaysForPlan]);

  function fmtCountdown(sec: number): string {
    const s = Math.max(0, Math.trunc(Number(sec) || 0));
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;
    if (hh > 0) return `${String(hh)}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  }

  // Show payment methods screen when CTA is clicked
  const handleShowPaymentMethods = () => {
    if (!selectedPlan) return;
    const telegramId = getTelegramId();
    if (!telegramId) {
      void requestAuthRequired();
      return;
    }
    setPaymentStep('methods');
    // Track payment methods view
    const selectionKind: 'explicit' | 'implicit' = didTrackPlanSelectedRef.current ? 'explicit' : 'implicit';
    void trackEvent('payment_methods_view', {
      origin: props.context.origin,
      metadata: {
        ...premiumAnalyticsMeta(selectedPlan, selectionKind),
        step: 'payment_methods',
      },
    });
  };

  // Handle Apple Pay / Google Pay selection
  const handleAppleGooglePay = async () => {
    if (!selectedPlan) return;
    setPaymentErr(null);
    const selectionKind: 'explicit' | 'implicit' = didTrackPlanSelectedRef.current ? 'explicit' : 'implicit';
    didAttemptPayRef.current = true;
    
    // Track payment method selection
    void trackEvent('payment_method_selected', {
      origin: props.context.origin,
      metadata: {
        ...premiumAnalyticsMeta(selectedPlan, selectionKind),
        payment_method: 'apple_google_pay',
        step: 'payment_method_selected',
      },
    });

    setPayState('preparing');

    try {
      const telegramUserId = getTelegramId();
      if (!telegramUserId) {
        setPaymentErr(reportHandledError({
          err: new Error('auth_required'),
          kind: 'auth',
          code: 'premium.auth_required',
          userMessage: t('auth.required_title'),
          severity: 'warning',
          context: { feature: 'premium', stage: 'purchase' },
        }));
        setPayState('ready');
        return;
      }
      
      // Create premium invoice via bot API. Only 30-day plans become recurring Stars subscriptions.
      const apiBaseUrl = services.config.config.apiBaseUrl;
      const clientSessionId = getClientSessionId();
      const res = await apiFetch(`${apiBaseUrl}/api/main/api/create-premium-invoice`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ planId: selectedPlan.id, userId: telegramUserId, locale, clientSessionId }),
      });
      const r = await res.json();

      if (r?.ok && r?.invoiceLink) {
        try {
          trackSubscriptionPaymentInitiated({
            origin: props.context.origin,
            selectionKind,
            provider: 'telegram',
            paymentMethod: 'apple_google_pay',
            providerPaymentMethod: 'telegram_stars',
            plan: selectedPlan,
            extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind) },
          });
        } catch {}

        // Save pending payment for feedback modal (cleared on callback)
        try {
          localStorage.setItem('pendingStarsPayment', JSON.stringify({
            plan_id: selectedPlan.id,
            created_at: Date.now(),
          }));
        } catch {}

        // Open Telegram payment
        (window as any).Telegram?.WebApp?.openInvoice?.(r.invoiceLink, (status: string) => {
          // Clear pending — we got a definitive answer
          try { localStorage.removeItem('pendingStarsPayment'); } catch {}

          if (status === 'paid') {
            didPayRef.current = true;
            setPayState('ready');
            setPaymentStep('cta');
            onClose();
            // Refresh subscription + balance without restarting the app
            try { window.dispatchEvent(new Event('aicams-subscription-refresh')); } catch {}
            try { window.dispatchEvent(new Event('aicams-credits-refresh')); } catch {}
            return;
          } else if (status === 'failed' || status === 'cancelled') {
            try {
              trackSubscriptionPaymentFailed({
                origin: props.context.origin,
                selectionKind,
                provider: 'telegram',
                paymentMethod: 'apple_google_pay',
                providerPaymentMethod: 'telegram_stars',
                status,
                plan: selectedPlan,
                extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind) },
              });
            } catch {}

            // Trigger post-checkout feedback for non-paid Stars (no visibilitychange here)
            try {
              window.dispatchEvent(new CustomEvent('aicams-checkout-feedback', {
                detail: { provider: 'telegram', planId: selectedPlan.id },
              }));
            } catch {}
          }
          setPayState('ready');
          setPaymentStep('cta');
        });
      } else {
        setPaymentErr(reportHandledError({
          err: new Error('invoice_create_failed'),
          kind: 'api',
          code: 'premium.invoice_failed',
          userMessage: t('premium.invoice_error'),
          severity: 'error',
          context: { feature: 'premium', endpoint: '/api/main/api/create-premium-invoice', hasInvoiceLink: Boolean((r as any)?.invoiceLink), resp: r },
        }));
        setPayState('ready');
      }
    } catch (e: any) {
      setPaymentErr(reportHandledError({
        err: e,
        kind: 'telegram_sdk',
        code: 'premium.payment_error',
        userMessage: e?.message || t('premium.payment_error'),
        severity: 'error',
        context: { feature: 'premium', endpoint: '/api/main/api/create-premium-invoice' },
      }));
      setPayState('ready');
    }
  };

  // Handle Crypto selection (NowPayments)
  const handleCrypto = async (payCurrencyOverride?: string, opts?: { reason?: 'initial' | 'asset_change' | 'new_payment' }) => {
    if (!selectedPlan) return;
    setPaymentErr(null);
    const selectionKind: 'explicit' | 'implicit' = didTrackPlanSelectedRef.current ? 'explicit' : 'implicit';
    didAttemptPayRef.current = true;

    const reason = opts?.reason || 'initial';
    const prevPayCurrency = String(cryptoSession?.pay_currency || '').trim().toLowerCase() || null;
    if (reason !== 'asset_change') {
      void trackEvent('payment_method_selected', {
        origin: props.context.origin,
        metadata: {
          ...(selectedPlan ? premiumAnalyticsMeta(selectedPlan, selectionKind) : {}),
          payment_method: 'crypto',
          step: 'payment_method_selected',
        },
      });
    }
    trackCryptoPaywallEvent('session_request_started', {
      reason,
      requested_pay_currency: payCurrencyOverride ? String(payCurrencyOverride).trim().toLowerCase() : null,
      previous_pay_currency: prevPayCurrency,
    });

    setPayState('preparing');

    try {
      // Always pass numeric plan id to avoid env-dependent name lookups on the backend.
      const planParam = String(selectedPlan.id);

      const jwt = await getAccessTokenOrNull(services);
      if (!jwt) {
        void requestAuthRequired();
        setPayState('ready');
        return;
      }

      const res = await apiFetch('/api/nowpayments/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          plan_id: planParam,
          pay_currency: payCurrencyOverride || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.ok && data?.ok && data?.session?.order_id) {
        const session: CryptoSession = {
          order_id: String(data.session.order_id || ''),
          payment_id: data.session.payment_id ? String(data.session.payment_id) : null,
          status: String(data.session.status || 'waiting'),
          pay_currency: data.session.pay_currency ? String(data.session.pay_currency) : null,
          network: data.session.network ? String(data.session.network) : null,
          pay_amount: Number.isFinite(Number(data.session.pay_amount)) ? Number(data.session.pay_amount) : null,
          pay_address: data.session.pay_address ? String(data.session.pay_address) : null,
          payin_extra_id: data.session.payin_extra_id ? String(data.session.payin_extra_id) : null,
          created_at: data.session.created_at ? String(data.session.created_at) : null,
          updated_at: data.session.updated_at ? String(data.session.updated_at) : null,
          expires_at: data.session.expires_at ? String(data.session.expires_at) : null,
        };
        setCryptoSession(session);
        setCryptoLastCheckedAtMs(Date.now());
        setCryptoLastCheckError(null);
        lastCryptoStatusRef.current = String(session.status || '').trim().toLowerCase() || null;
        persistPendingCrypto(session.order_id, session.created_at ? new Date(session.created_at).getTime() : Date.now());

        trackCryptoPaywallEvent('session_ready', {
          reason,
          reused: Boolean(data.reused),
          order_id: session.order_id || null,
          payment_id: session.payment_id || null,
          pay_currency: session.pay_currency || null,
          network: session.network || null,
          status: String(session.status || '').trim().toLowerCase() || null,
        });
        if (reason === 'asset_change') {
          trackCryptoPaywallEvent('asset_changed', {
            previous_pay_currency: prevPayCurrency,
            pay_currency: session.pay_currency || null,
            network: session.network || null,
          });
        } else {
          try {
            trackSubscriptionPaymentInitiated({
              origin: props.context.origin,
              selectionKind,
              provider: 'nowpayments',
              paymentMethod: 'crypto',
              providerPaymentMethod: 'nowpayments',
              plan: selectedPlan,
              refs: {
                order_id: session.order_id || null,
                invoice_id: session.payment_id || null,
                reused: Boolean(data.reused),
              },
              extraMetadata: {
                ...premiumAnalyticsMeta(selectedPlan, selectionKind),
                pay_currency: session.pay_currency,
                network: session.network,
              },
            });
          } catch {}
        }

        setPaymentStep('crypto');
        setPayState('ready');
      } else {
        console.error('[crypto] session error:', data);
        trackCryptoPaywallEvent('session_request_failed', {
          reason,
          api_error: String(data?.error || 'session_create_failed'),
          nowpayments_status: data?.nowpayments_status ?? null,
          nowpayments_error: data?.nowpayments_error ?? null,
          http_status: res.status,
          requested_pay_currency: payCurrencyOverride ? String(payCurrencyOverride).trim().toLowerCase() : null,
        });
        try {
          trackSubscriptionPaymentFailed({
            origin: props.context.origin,
            selectionKind,
            provider: 'nowpayments',
            paymentMethod: 'crypto',
            providerPaymentMethod: 'nowpayments',
            status: String(data?.error || 'session_create_failed'),
            plan: selectedPlan,
            refs: { order_id: data?.order_id || null, invoice_id: data?.payment_id || null },
            extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind), resp: data },
          });
        } catch {}
        const apiErr = String(data?.error || '');
        const baseMessage =
          apiErr === 'auth_required' ? t('common.auth_required') :
          apiErr === 'missing_plan_id' ? t('premium.payment_error') :
          apiErr === 'invalid_plan_id' ? t('premium.payment_error') :
          apiErr === 'invalid_pay_currency' ? t('premium.payment_error') :
          t('premium.payment_error');
        const npStatus = (apiErr === 'nowpayments_api_error' && data?.nowpayments_status)
          ? String(data.nowpayments_status)
          : '';
        const npError = (apiErr === 'nowpayments_api_error' && data?.nowpayments_error)
          ? String(data.nowpayments_error)
          : '';
        const debugCode = apiErr
          ? (npStatus ? `${apiErr}:np${npStatus}:${res.status}` : `${apiErr}:${res.status}`)
          : `http:${res.status}`;
        const debugTail = npError ? ` — ${npError}` : '';
        const userMessage = baseMessage === t('common.auth_required') ? baseMessage : `${baseMessage} (${debugCode})${debugTail}`;
        setPaymentErr(reportHandledError({
          err: new Error(apiErr ? `crypto_session_failed:${apiErr}` : 'crypto_session_failed'),
          kind: 'api',
          code: 'premium.crypto_session_failed',
          userMessage,
          severity: 'error',
          context: { feature: 'premium', endpoint: '/api/nowpayments/session', resp: data, httpStatus: res.status },
        }));
        setPayState('ready');
      }
    } catch (e: any) {
      console.error('[crypto] error:', e);
      trackCryptoPaywallEvent('session_request_exception', {
        reason,
        requested_pay_currency: payCurrencyOverride ? String(payCurrencyOverride).trim().toLowerCase() : null,
        error: String(e?.message || e || ''),
      });
      try {
        trackSubscriptionPaymentFailed({
          origin: props.context.origin,
          selectionKind,
          provider: 'nowpayments',
          paymentMethod: 'crypto',
          providerPaymentMethod: 'nowpayments',
          status: 'invoice_create_error',
          plan: selectedPlan,
          extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind), error: String(e?.message || e || '') },
        });
      } catch {}
      setPaymentErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'premium.crypto_error',
        userMessage: e?.message || t('premium.payment_error'),
        severity: 'error',
        context: { feature: 'premium', endpoint: '/api/nowpayments/session' },
      }));
      setPayState('ready');
    }
  };

  const loadCryptoAssets = async () => {
    if (cryptoAssetsLoading) return;
    setCryptoAssetsErr(null);
    setCryptoAssetsLoading(true);
    try {
      const jwt = await getAccessTokenOrNull(services);
      if (!jwt) {
        void requestAuthRequired();
        return;
      }
      const res = await apiFetch('/api/nowpayments/assets?limit=1000', {
        method: 'GET',
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        throw new Error(String(data?.error || `http_${res.status}`));
      }
      const list = Array.isArray(data?.assets) ? data.assets : [];
      setCryptoAssets(list as CryptoAsset[]);
    } catch (e: any) {
      setCryptoAssetsErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'premium.crypto_assets_failed',
        userMessage: t('premium.payment_error'),
        severity: 'warning',
        context: { feature: 'premium', endpoint: '/api/nowpayments/assets' },
      }));
    } finally {
      setCryptoAssetsLoading(false);
    }
  };

  useEffect(() => {
    if (paymentStep !== 'crypto') return;
    if (!cryptoSession?.pay_currency) return;
    if (cryptoAssets) return;
    void loadCryptoAssets();
  }, [paymentStep, cryptoSession?.pay_currency]);

  useEffect(() => {
    if (!selectedPlan?.id) return;
    const orderId = cryptoSession?.order_id;
    if (!orderId) return;
    const planIdFromOrder = parseNowPaymentsPlanIdFromOrderId(orderId);
    if (!planIdFromOrder) return;
    if (String(planIdFromOrder) === String(selectedPlan.id)) return;

    void cancelCryptoSession(orderId, 'plan_changed');
    clearPendingCrypto();
    setCryptoSession(null);
    setCryptoLastCheckedAtMs(null);
    setCryptoLastCheckError(null);
  }, [selectedPlan?.id, cryptoSession?.order_id]);

  useEffect(() => {
    if (!cryptoAssetPickerOpen) return;
    if (cryptoAssets) return;
    void loadCryptoAssets();
  }, [cryptoAssetPickerOpen]);

  useEffect(() => {
    if (paymentStep !== 'crypto') return;
    const viewKey = String(cryptoSession?.order_id || '__no_order__');
    if (lastCryptoViewOrderRef.current === viewKey) return;
    lastCryptoViewOrderRef.current = viewKey;
    trackCryptoPaywallEvent('crypto_step_view', {
      order_id: cryptoSession?.order_id || null,
      payment_id: cryptoSession?.payment_id || null,
      pay_currency: cryptoSession?.pay_currency || null,
      network: cryptoSession?.network || null,
      status: String(cryptoSession?.status || '').trim().toLowerCase() || null,
    });
  }, [paymentStep, cryptoSession?.order_id, cryptoSession?.payment_id, cryptoSession?.pay_currency, cryptoSession?.network, cryptoSession?.status]);

  // Handle Card selection via CentroBill hosted checkout
  const handleCard = async () => {
    if (!selectedPlan) return;
    setPaymentErr(null);
    const selectionKind: 'explicit' | 'implicit' = didTrackPlanSelectedRef.current ? 'explicit' : 'implicit';
    didAttemptPayRef.current = true;

    // Reuse pending card payment only for the currently selected plan.
    const pending = readPendingCardPaymentForPlan(selectedPlan.id);
    if (pending) {
      const tg = (window as any).Telegram?.WebApp;
      if (tg?.openLink) tg.openLink(pending.paymentUrl);
      else window.open(pending.paymentUrl, '_blank');

      setCurrentOrderUuid(pending.orderId);
      setHasPendingCard(true);
      setPaymentStep('cta');
      return;
    }
    
    void trackEvent('payment_method_selected', {
      origin: props.context.origin,
      metadata: {
        ...(selectedPlan ? premiumAnalyticsMeta(selectedPlan, selectionKind) : {}),
        payment_method: 'card',
        step: 'payment_method_selected',
      },
    });

    setPayState('preparing');

    try {
      const token = await getAccessTokenOrNull(services);
      if (!token) {
        void requestAuthRequired();
        setPayState('ready');
        return;
      }

      const clientSessionId = getClientSessionId();
      const res = await apiFetch('/api/centrobill/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ plan_id: selectedPlan.id, client_session_id: clientSessionId }),
      });
      const data = await res.json();

      if (data?.ok && data?.payment_url) {
        try {
          trackSubscriptionPaymentInitiated({
            origin: props.context.origin,
            selectionKind,
            provider: 'centrobill',
            paymentMethod: 'card',
            providerPaymentMethod: 'centrobill',
            plan: selectedPlan,
            refs: { order_id: data.order_id || null, reused: Boolean(data.reused) },
            extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind) },
          });
        } catch {}

        // Save pending payment so user can continue later (same UX as crypto).
        try {
          localStorage.setItem('pendingCardPayment', JSON.stringify({
            order_id: data.order_id,
            payment_url: data.payment_url,
            plan_id: selectedPlan.id,
            provider: 'centrobill',
            created_at: Boolean(data.reused)
              ? (JSON.parse(localStorage.getItem('pendingCardPayment') || '{}')?.created_at || Date.now())
              : Date.now(),
          }));
          setHasPendingCard(true);
        } catch {}

        // Open payment URL immediately (single-step UX).
        // NOTE: Telegram iOS can be strict about "openLink" needing a direct gesture; user requested no extra step.
        try {
          const tg = (window as any).Telegram?.WebApp;
          if (tg?.openLink) tg.openLink(data.payment_url);
          else window.open(data.payment_url, '_blank');
        } catch {}

        setCurrentOrderUuid(data.order_id || null);
        setPaymentStep('cta');
        setPayState('ready');
      } else {
        console.error('[card] create order error:', data);
        try {
          trackSubscriptionPaymentFailed({
            origin: props.context.origin,
            selectionKind,
            provider: 'centrobill',
            paymentMethod: 'card',
            providerPaymentMethod: 'centrobill',
            status: String(data?.error || 'order_create_failed'),
            plan: selectedPlan,
            extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind), resp: data },
          });
        } catch {}
        setPaymentErr(reportHandledError({
          err: new Error('card_order_failed'),
          kind: 'api',
          code: 'premium.card_order_failed',
          userMessage: data?.message || t('premium.payment_error'),
          severity: 'error',
          context: { feature: 'premium', endpoint: '/api/centrobill/create-order', resp: data },
        }));
        setPayState('ready');
      }
    } catch (e: any) {
      console.error('[card] error:', e);
      try {
        trackSubscriptionPaymentFailed({
          origin: props.context.origin,
          selectionKind,
          provider: 'centrobill',
          paymentMethod: 'card',
          providerPaymentMethod: 'centrobill',
          status: 'order_create_error',
          plan: selectedPlan,
          extraMetadata: { ...premiumAnalyticsMeta(selectedPlan, selectionKind), error: String(e?.message || e || '') },
        });
      } catch {}
      setPaymentErr(reportHandledError({
        err: e,
        kind: 'api',
        code: 'premium.card_error',
        userMessage: e?.message || t('premium.payment_error'),
        severity: 'error',
        context: { feature: 'premium', endpoint: '/api/centrobill/create-order' },
      }));
      setPayState('ready');
    }
  };

  const handleRefreshCryptoStatus = async () => {
    if (!cryptoSession?.order_id) return;
    trackCryptoPaywallEvent('manual_check_clicked', {
      order_id: cryptoSession.order_id,
    });
    try {
      await checkCryptoStatusNow('manual');
      setCryptoRefreshTick((v) => v + 1);
    } catch (e) {
      console.error('[crypto] manual refresh error:', e);
    }
  };

  const onClose = () => {
    const openedAt = openedAtRef.current;
    const timeSpentMs = openedAt ? Math.max(0, Date.now() - openedAt) : 0;
    const origin = props.context.origin;
    if (origin === 'chat' && !didAttemptPayRef.current && !didPayRef.current) {
      try {
        trackPaywallDismissed({ timeSpentMs, origin, packageId: selectedId });
      } catch {}
    }
    props.onClose();
  };

  const isChat = props.context.origin === 'chat';
  const commandName = isChat ? (props.context as any).commandName : null;

  return (
    <BottomSheet open={props.open} onClose={onClose}>
      {/* Card Payment Result Toast */}
      {cardPaymentResult && (
        <div className={`payment-toast ${cardPaymentResult}`}>
          {cardPaymentResult === 'success' ? '✓' : '✗'}
        </div>
      )}

      <div className="premium-sheet premium-sheet-sticky premium-compact">
        <div className="premium-scroll">
          {/* Header */}
          <div className="premium-header">
            <div className="premium-crown">👑</div>
            <h2 className="premium-title">{t('premium.title')}</h2>
            {isChat && commandName && (
              <p className="premium-subtitle">
                "{commandName}" {t('premium.command_only')}
              </p>
            )}
            {!isChat && (
              <p className="premium-subtitle">
                {t('premium.subtitle')}
              </p>
            )}
          </div>

          {/* Limited Offer (compact split pill: 50/50 label | timer) */}
          {(offerState === 'active' || offerState === 'grace') && (giftMonthsForPlan > 0 || bonusCreditsForPlan > 0) && (
            <div className={`premium-offerPill ${offerState === 'grace' ? 'grace' : ''}`} role="status" aria-live="polite">
              <div className="premium-offerPillLeft">{t('premium.offer_badge')}</div>
              <div className="premium-offerPillRight">
                <span className="premium-offerPillClock" aria-hidden="true">⏳</span>
                <span className="premium-offerPillLabel">
                  {offerState === 'grace' ? t('premium.offer_grace') : t('premium.offer_ends_in')}
                </span>
                <span className="premium-offerPillTime">
                  {fmtCountdown(offerState === 'grace' ? offerGraceSecondsLeft : offerSecondsLeft)}
                </span>
              </div>
            </div>
          )}

          {/* Grant-based persistent offer (no timer) */}
          {offerState === 'grant_active' && (giftMonthsForPlan > 0 || bonusCreditsForPlan > 0) && (
            <div className="premium-offerPill" role="status" aria-live="polite">
              <div className="premium-offerPillLeft">🎁 {t('premium.offer_badge')}</div>
              <div className="premium-offerPillRight">
                <span className="premium-offerPillLabel" style={{ fontWeight: 600 }}>
                  {t('premium.offer_personal') || 'Personal offer'}
                </span>
              </div>
            </div>
          )}

          {paymentStep !== 'crypto' && (
            <>
              {/* Benefits */}
              <div className="premium-benefits">
                <div className="benefit-item">
                  <span className="benefit-icon">💬</span>
                  <span>{t('premium.benefit_chat')}</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">⭐</span>
                  <span>{t('premium.benefit_commands')}</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🎁</span>
                  <span>{t('premium.benefit_credits')}</span>
                </div>
                <div className="benefit-item">
                  <span className="benefit-icon">🚀</span>
                  <span>{t('premium.benefit_early')}</span>
                </div>
              </div>

              {/* Plans */}
              <div className="premium-plans">
                {loading && <div className="muted">{t('common.loading')}</div>}
                {plansErr && (
                  <div className="card modelMeta">
                    <div className="muted">{plansErr}</div>
                    <div className="taskActionsRow">
                      <button className="secondaryBtn" onClick={() => setReloadKey((v) => v + 1)}>
                        {t('common.retry')}
                      </button>
                    </div>
                  </div>
                )}
                {!loading && !plansErr && (() => {
                  const regularPlans = plans.filter(p => p.duration_days < 3650);
                  const lifetimePlans = plans.filter(p => p.duration_days >= 3650);

                  function renderCard(plan: SubscriptionPlan) {
                    const isLifetime = plan.duration_days >= 3650;
                    return (
                      <button
                        key={plan.id}
                        className={`plan-card ${selectedId === plan.id ? 'selected' : ''} ${plan.is_popular ? 'popular' : ''} ${isLifetime ? 'lifetime' : ''} ${plan.duration_days <= 7 ? 'daily' : ''}`}
                        onClick={() => {
                          setPaymentErr(null);
                          setSelectedId(plan.id);
                          didTrackPlanSelectedRef.current = true;
                          try {
                            trackPackageSelected(plan.id, plan.bonus_credits, plan.price_stars, props.context.origin, undefined, premiumAnalyticsMeta(plan, 'explicit'));
                          } catch {}
                        }}
                      >
                        {isLifetime && <div className="plan-badge plan-badge-lifetime">💰 {t('premium.lifetime_badge')}</div>}
                        {plan.is_popular && !isLifetime && <div className="plan-badge">{t('premium.best_value')}</div>}
                        {plan.duration_days <= 7 && !isLifetime && !plan.is_popular && <div className="plan-badge plan-badge-daily">⚡ {t('premium.daily_badge')}</div>}
                        <div className="plan-name">
                          {isLifetime && <span className="plan-name-emoji">🤑</span>}
                          {plan.name}
                        </div>
                        {isLifetime && <div className="plan-subtitle-forever">{t('premium.forever_access')}</div>}
                        <div className="plan-bonus plan-bonus-big">
                          <div className="plan-bonus-main">
                            +{plan.bonus_credits.toLocaleString()} <span className="plan-bonus-free">{t('premium.free_label')}</span> {t('premium.credits')}
                          </div>
                        </div>

                        {(offerState === 'active' || offerState === 'grace' || offerState === 'grant_active') && (() => {
                          const d = Number(plan.duration_days || 0);
                          const isDaily = d <= 7;
                          const isYearly = d >= 300;
                          let gm2 = 0, gd2 = 0, bc2 = 0;
                          if (isDaily) {
                            const gd = Number(offer?.gift_days_daily || 0);
                            gd2 = Number.isFinite(gd) ? Math.max(0, Math.trunc(gd)) : 0;
                            const bc = Number(offer?.bonus_credits_daily || 0);
                            bc2 = Number.isFinite(bc) ? Math.max(0, Math.trunc(bc)) : 0;
                          } else {
                            const gm = isYearly ? Number(offer?.gift_months_yearly || 0) : Number(offer?.gift_months_monthly || 0);
                            gm2 = Number.isFinite(gm) ? Math.max(0, Math.trunc(gm)) : 0;
                            const bc = isYearly ? Number(offer?.bonus_credits_yearly || 0) : Number(offer?.bonus_credits_monthly || 0);
                            bc2 = Number.isFinite(bc) ? Math.max(0, Math.trunc(bc)) : 0;
                          }
                          if (gm2 <= 0 && gd2 <= 0 && bc2 <= 0) return null;
                          return (
                            <div className="plan-offer">
                              <div className="plan-offerBadge">{t('premium.offer_badge')}</div>
                              <div className="plan-offerLines">
                                {gm2 > 0 && (
                                  <div className="plan-offerLine">
                                    {(() => {
                                      if (isYearly && gm2 >= 12 && gm2 % 12 === 0) {
                                        const years = Math.trunc(gm2 / 12);
                                        return t('premium.offer_years_free').replace('{years}', String(years));
                                      }
                                      return t('premium.offer_months_free').replace('{months}', String(gm2));
                                    })()}
                                  </div>
                                )}
                                {gd2 > 0 && (
                                  <div className="plan-offerLine">
                                    {t('premium.offer_days_free').replace('{days}', String(gd2))}
                                  </div>
                                )}
                                {bc2 > 0 && (
                                  <div className="plan-offerLine">
                                    {t('premium.offer_bonus_credits').replace('{credits}', bc2.toLocaleString())}
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        <div className="plan-priceRow">
                          <span className="plan-priceRowAmount">
                            {fmtMoney(plan.price_amount, plan.price_currency)} / {plan.price_stars} ⭐
                          </span>
                        </div>
                      </button>
                    );
                  }

                  return (
                    <>
                      {regularPlans.length > 0 && (
                        <div className={`premium-plans-row ${regularPlans.length === 1 ? 'single' : ''}`}>
                          {regularPlans.map(renderCard)}
                        </div>
                      )}
                      {lifetimePlans.map(renderCard)}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>

        <div className="premium-footer">
          {/* Payment Methods Screen */}
          {paymentStep === 'methods' && selectedPlan ? (
            <div className="premium-payment-methods">
              {paymentErr && (
                <div className="card modelMeta">
                  <div className="muted">{paymentErr}</div>
                  <div className="taskActionsRow">
                    <button className="taskActionBtn taskActionBtnOpen" onClick={() => setPaymentErr(null)}>
                      {t('common.dismiss')}
                    </button>
                  </div>
                </div>
              )}
              <button
                className="premium-method-btn primary"
                onClick={handleCard}
                disabled={payState === 'preparing'}
              >
                <span className="method-icon">{hasPendingCard ? '⏳' : '💳'}</span>
                {hasPendingCard ? t('premium.card_continue') : t('premium.pay_card')}
              </button>

              <button
                className="premium-method-btn secondary"
                onClick={handleAppleGooglePay}
                disabled={payState === 'preparing'}
              >
                {payState === 'preparing' ? (
                  t('premium.preparing')
                ) : (
                  <>
                    <span className="method-icon">⭐</span>
                    {t('premium.pay_apple_google')}
                  </>
                )}
              </button>

              <button
                className="premium-method-btn secondary"
                onClick={() => {
                  if (hasPendingCryptoForSelectedPlan && cryptoSession?.order_id) {
                    trackCryptoPaywallEvent('continue_pending_clicked', {
                      order_id: cryptoSession.order_id,
                      pay_currency: cryptoSession.pay_currency || null,
                    });
                    setPaymentStep('crypto');
                    return;
                  }
                  void handleCrypto();
                }}
                disabled={payState === 'preparing'}
              >
                <span className="method-icon">{hasPendingCryptoForSelectedPlan ? '⏳' : '₿'}</span>
                {hasPendingCryptoForSelectedPlan
                  ? t('premium.crypto_continue') 
                  : t('premium.pay_crypto')}
              </button>
            </div>
          ) : paymentStep === 'crypto' ? (
            <div className="premium-payment-methods">
              <div className="muted">
                {t('premium.crypto_choose_asset_hint')}
              </div>

              <button
                className="listBtn assetBtn"
                onClick={() => {
                  setCryptoAssetQuery('');
                  setCryptoAssetPickerOpen(true);
                  trackCryptoPaywallEvent('asset_picker_opened');
                }}
                disabled={payState === 'preparing' || cryptoManualChecking}
              >
                <div className="assetRow">
                  <div className="assetIcon" aria-hidden="true">
                    {(() => {
                      const cur = String(cryptoSession?.pay_currency || '').trim().toLowerCase();
                      const a = cryptoAssets?.find((x) => String(x.pay_currency || '').toLowerCase() === cur) || null;
                      const url = a?.logo_url || null;
                      return url ? <img src={url} alt="" loading="lazy" /> : <span className="mono">{cur ? cur.slice(0, 1).toUpperCase() : '₿'}</span>;
                    })()}
                  </div>
                  <div className="assetMeta">
                    <div className="assetCodeRow">
                      <div className="mono assetCode">
                        {cryptoSession?.pay_currency
                          ? String(cryptoSession.pay_currency).toUpperCase()
                          : t('premium.crypto_asset_choose')}
                      </div>
                      {(() => {
                        const cur = String(cryptoSession?.pay_currency || '').trim().toLowerCase();
                        const a = cryptoAssets?.find((x) => String(x.pay_currency || '').toLowerCase() === cur) || null;
                        const badge = networkBadgeLabel(a?.network);
                        return badge ? <span className="assetBadge">{badge}</span> : null;
                      })()}
                    </div>
                    {(() => {
                      const cur = String(cryptoSession?.pay_currency || '').trim().toLowerCase();
                      const a = cryptoAssets?.find((x) => String(x.pay_currency || '').toLowerCase() === cur) || null;
                      const name = String(a?.name || a?.ticker || '').trim();
                      if (!name) return null;
                      return <div className="assetName">{name}</div>;
                    })()}
                  </div>
                </div>
                <div className="muted">›</div>
              </button>

              {cryptoSession?.order_id && (
                <div className="muted">
                  {t('premium.crypto_step_hint')}
                </div>
              )}

              {cryptoSession?.order_id && (
                <div className="card modelMeta">
                  <div className="filterField">
                    {cryptoLastCheckError && (
                      <div className="muted">
                        {t('premium.crypto_check_error')}
                      </div>
                    )}

                    <div className="row">
                      <div className="muted">{t('premium.crypto_amount_label')}</div>
                      <div className="hstack">
                        <strong className="mono">
                          {cryptoSession.pay_amount !== null
                            ? String(cryptoSession.pay_amount)
                            : '—'}
                        </strong>
                        {cryptoSession.pay_amount !== null && (
                          <button
                            className="iconBtn"
                            onClick={() => copyText(String(cryptoSession.pay_amount), t('premium.crypto_amount_label'), 'amount')}
                            aria-label={t('premium.copy')}
                          >
                            <IconCopy />
                          </button>
                        )}
                      </div>
                    </div>

                    {cryptoSession.pay_address && (
                      <div className="filterField">
                        <div className="row">
                          <div className="muted">{t('premium.crypto_address_label')}</div>
                          <div className="hstack">
                            <strong className="mono">{shortenMiddle(String(cryptoSession.pay_address || ''), 6, 6)}</strong>
                            <button
                              className="iconBtn"
                              onClick={() => copyText(String(cryptoSession.pay_address || ''), t('premium.crypto_address_label'), 'address')}
                              aria-label={t('premium.copy')}
                            >
                              <IconCopy />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {cryptoSession.payin_extra_id && (
                      <div className="filterField">
                        <div className="row">
                          <div className="muted">{t('premium.crypto_memo_label')}</div>
                          <button
                            className="iconBtn"
                            onClick={() => copyText(String(cryptoSession.payin_extra_id || ''), t('premium.crypto_memo_label'), 'memo')}
                            aria-label={t('premium.copy')}
                          >
                            <IconCopy />
                          </button>
                        </div>
                        <div className="monoWrap">{cryptoSession.payin_extra_id}</div>
                        <div className="muted">
                          {t('premium.crypto_memo_required')}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <button
                className="premium-method-btn primary"
                onClick={handleRefreshCryptoStatus}
                disabled={payState === 'preparing' || cryptoManualChecking || !cryptoSession?.order_id}
              >
                {cryptoManualChecking
                  ? t('premium.crypto_checking')
                  : t('premium.crypto_ive_paid')}
              </button>

              <button
                className="premium-method-btn secondary"
                onClick={() => {
                  trackCryptoPaywallEvent('switch_method_clicked', {
                    order_id: cryptoSession?.order_id || null,
                  });
                  void cancelCryptoSession(cryptoSession?.order_id, 'switch_method');
                  clearPendingCrypto();
                  setCryptoSession(null);
                  setCryptoLastCheckedAtMs(null);
                  setCryptoLastCheckError(null);
                  setPaymentStep('methods');
                }}
                disabled={payState === 'preparing' || cryptoManualChecking}
              >
                {t('premium.crypto_choose_other_method')}
              </button>
            </div>
          ) : (
            /* CTA Button */
            selectedPlan && (
              <button
                className="premium-cta"
                onClick={handleShowPaymentMethods}
                disabled={payState === 'preparing'}
              >
                {isLifetime
                  ? t('premium.cta_billionaire')
                    .replace('{price}', fmtMoney(selectedPlan.price_amount, selectedPlan.price_currency))
                    .replace('{stars}', String(selectedPlan.price_stars))
                  : t('premium.cta_premium')
                    .replace('{price}', fmtMoney(selectedPlan.price_amount, selectedPlan.price_currency))
                    .replace('{stars}', String(selectedPlan.price_stars))}
              </button>
            )
          )}
        </div>

        {/* Toast for messages */}
        {cryptoAssetPickerOpen && typeof document !== 'undefined' && createPortal(
          <BottomSheet
            open={true}
            onClose={() => setCryptoAssetPickerOpen(false)}
            ariaLabel={t('premium.crypto_asset_title')}
            className="assetPickerModal"
            sheetClassName="assetPickerSheet"
          >
            <div className="assetPickerRoot">
              <div className="assetPickerHeader">
                <div className="assetPickerTitle">{t('premium.crypto_asset_title')}</div>
                <button className="iconBtn" onClick={() => setCryptoAssetPickerOpen(false)} aria-label={t('btn.close')}>
                  <IconClose />
                </button>
              </div>

              <div className="assetPickerSearch">
                <div className="row">
                  <div className="muted" aria-hidden="true">
                    <IconSearch />
                  </div>
                  <input
                    className="textInput"
                    value={cryptoAssetQuery}
                    onInput={(e) => setCryptoAssetQuery((e.currentTarget as HTMLInputElement).value)}
                    placeholder={t('premium.crypto_asset_search')}
                  />
                </div>
              </div>

              <div className="assetPickerList">
                {cryptoAssetsLoading && <div className="muted">{t('common.loading')}</div>}
                {cryptoAssetsErr && <div className="muted">{cryptoAssetsErr}</div>}

                {!cryptoAssetsLoading && !cryptoAssetsErr && cryptoAssets && (() => {
                  const q = String(cryptoAssetQuery || '').trim().toLowerCase();
                  const filtered = q
                    ? cryptoAssets.filter((a) => {
                      const code = String(a.code || '').toLowerCase();
                      const ticker = String(a.ticker || '').toLowerCase();
                      const name = String(a.name || '').toLowerCase();
                      const network = String(a.network || '').toLowerCase();
                      const pc = String(a.pay_currency || '').toLowerCase();
                      return code.includes(q) || ticker.includes(q) || name.includes(q) || network.includes(q) || pc.includes(q);
                    })
                    : cryptoAssets;
                  return (
                    <>
                      {filtered.map((a) => {
                        const current = String(cryptoSession?.pay_currency || '').trim().toLowerCase();
                        const selected = current === String(a.pay_currency || '').trim().toLowerCase();
                        const badge = networkBadgeLabel(a.network);
                        return (
                          <button
                            key={a.pay_currency}
                            className="listBtn assetBtn"
                            onClick={() => {
                              setCryptoAssetPickerOpen(false);
                              if (selected) return;
                              void handleCrypto(String(a.pay_currency), { reason: 'asset_change' });
                            }}
                            disabled={payState === 'preparing' || cryptoManualChecking}
                          >
                            <div className="assetRow">
                              <div className="assetIcon" aria-hidden="true">
                                {a.logo_url ? <img src={a.logo_url} alt="" loading="lazy" /> : <span className="mono">{String(a.code || a.pay_currency || '').slice(0, 1)}</span>}
                              </div>
                              <div className="assetMeta">
                                <div className="assetCodeRow">
                                  <div className="mono assetCode">{String(a.code || a.pay_currency || '').toUpperCase()}</div>
                                  {badge ? <span className="assetBadge">{badge}</span> : null}
                                </div>
                                <div className="assetName">
                                  {String(a.name || a.ticker || '')}
                                </div>
                              </div>
                            </div>
                            <div className="muted" aria-hidden="true">{selected ? '✓' : ''}</div>
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
            </div>
          </BottomSheet>,
          document.body
        )}

        {toastMessage && (
          <div className={`premium-toast ${toastType === 'success' ? 'premium-toast-success' : ''}`}>
            {toastType === 'success' && <span className="toast-icon">✓</span>}
            {toastMessage}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
